import {
  Component,
  ChangeDetectionStrategy,
  input,
  inject
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ZefReactiveComponent } from '@zerops/zef/core';
import {
  PipelineDebugModes,
  PipelineDebugPhases,
  PipelineDebugPhasesUserDataKeys,
  ServiceStackEntity,
  pipelineDebug
} from '@zerops/zerops/core/service-stack-base';
import { UserDataEntity } from '@zerops/zerops/core/user-data-base';
import {
  Subject,
  distinctUntilKeyChanged,
  filter,
  map,
  of,
  switchMap,
  timer,
  withLatestFrom
} from 'rxjs';
import { FEATURE_NAME } from './debug-switcher.constant';
import { ZefProgress, selectZefProgressesByType } from '@zerops/zef/progress';
import { Store, select } from '@ngrx/store';
import { hasBasicUserRoleOrHigher } from '@zerops/models/client-user';
import { distinctUntilChanged } from 'rxjs/operators';
import { UserEntity } from '@zerops/zerops/core/user-base';

@Component({
  selector: 'z-debug-switcher',
  templateUrl: './debug-switcher.feature.html',
  styleUrl: './debug-switcher.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DebugSwitcherFeature extends ZefReactiveComponent {

  // # Deps
  #serviceStackEntity = inject(ServiceStackEntity);
  #userDataEntity = inject(UserDataEntity);
  #userEntity = inject(UserEntity);
  #store = inject(Store);

  // # Event Streams
  onSetDebug$ = new Subject<{
    phase: PipelineDebugPhases;
    mode: PipelineDebugModes
  }>();

  // # Data
  // -- sync
  debugPhases = PipelineDebugPhases;
  debugModes = PipelineDebugModes;
  debugPhasesKeys = PipelineDebugPhasesUserDataKeys;
  options = [
    {
      label: 'Disable',
      mode: this.debugModes.NONE
    },
    {
      label: 'Before first command',
      mode: this.debugModes.BEFORE
    },
    {
      label: 'After last command',
      mode: this.debugModes.AFTER
    },
    {
      label: 'On command fail',
      mode: this.debugModes.ON_FAIL
    }
  ];

  // -- angular
  serviceId = input<string>();
  serviceId$ = toObservable(this.serviceId);

  // -- async
  service$ = this.serviceId$.pipe(
    filter((d) => !!d),
    switchMap((id) => this.#serviceStackEntity.entityById$(id)),
    filter((d) => !!d)
  );
  pipelineDebugUserDataMap$ = this.service$.pipe(
    switchMap((service) => this.#userDataEntity.list$({ name: FEATURE_NAME, id: service.id })),
    filter((d) => !!d),
    map((userData) => userData?.reduce((result, item) => {
        if (
          item.key === this.debugPhasesKeys.DEBUG_BUILD ||
          item.key === this.debugPhasesKeys.DEBUG_RUNTIME_PREPARE
        ) {
          result[item.key as PipelineDebugPhasesUserDataKeys] = item.content as PipelineDebugModes;
        }
        return result;
      }, {
        [this.debugPhasesKeys.DEBUG_BUILD]: undefined,
        [this.debugPhasesKeys.DEBUG_RUNTIME_PREPARE]: undefined
      } as Record<PipelineDebugPhasesUserDataKeys, PipelineDebugModes>
    ))
  );
  progresses$ = this.#store.pipe(
    select(selectZefProgressesByType(pipelineDebug.type)),
    map((loadings) => loadings?.reduce((res, itm) => {
      if (itm.meta === this.debugPhases.DEBUG_BUILD && !res.debugBuild) {
        res.debugBuild = itm;
      }
      if (itm.meta === this.debugPhases.DEBUG_RUNTIME_PREPARE && !res.debugBuild) {
        res.debugRuntimePrepare = itm;
      }
      return res;
    }, {
      [this.debugPhases.DEBUG_BUILD]: undefined,
      [this.debugPhases.DEBUG_RUNTIME_PREPARE]: undefined
    } as {
      debugBuild?: ZefProgress;
      debugRuntimePrepare?: ZefProgress;
    }))
  );
  buildDebugLoading$ = this.progresses$.pipe(
    switchMap((loadings) => !loadings?.debugBuild
      ? timer(500).pipe(map(() => loadings.debugBuild))
      : of(loadings.debugBuild)
  ));
  runtimePrepareDebugLoading$ = this.progresses$.pipe(
    switchMap((loadings) => !loadings?.debugRuntimePrepare
      ? timer(500).pipe(map(() => loadings.debugRuntimePrepare))
      : of(loadings.debugRuntimePrepare)
  ));
  // Role Permission
  userPermission$ = this.#userEntity.activeClientUser$.pipe(
    filter((d) => !!d),
    map((u) => ({
      role: u.roleCode,
      canCreate: u.canCreateProjects || false
    })),
    distinctUntilChanged()
  );
  hasDeveloperRoleOrHigher$ = this.userPermission$.pipe(
    map(({ role }) => hasBasicUserRoleOrHigher(role))
  );

  // # State resolver
  state = this.$connect({
    pipelineDebugUserDataMap: this.pipelineDebugUserDataMap$,
    buildDebugLoading: this.buildDebugLoading$,
    runtimprepareDebugLoading: this.runtimePrepareDebugLoading$,
    hasDeveloperRoleOrHigher: this.hasDeveloperRoleOrHigher$
  });

  constructor() {
    super();

    this.$dispatchActions([

      this.onSetDebug$.pipe(
        withLatestFrom(toObservable(this.serviceId)),
        map(([ { phase, mode }, id ]) => pipelineDebug(
          { id, phase, mode },
          undefined,
          { meta: phase }
        ))
      ),

      this.service$.pipe(
        filter((d) => !!d),
        distinctUntilKeyChanged('id'),
        map(({ id, project }) => this.#userDataEntity.listSubscribe(
          project.clientId,
          {
            name: FEATURE_NAME,
            id
          },
          {
            search: [
              {
                name: 'serviceStackId',
                operator: 'eq',
                value: id
              }
            ]
          }
        ))

      )
    ]);

  }
}
